import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Box, Typography, Paper, Grid, Alert } from '@mui/material';
import confetti from 'canvas-confetti';
import {
  validateMemoryGameData,
  isValidMemoryGameData,
  createSampleMemoryGameData,
} from '../utils/memoryGameUtils';
import { useTranslation } from 'react-i18next';

const MemoryGame = ({ options, onChange }) => {
  const { t } = useTranslation(['lessons']);

  // Создаем стабильный ID для компонента, чтобы избежать проблем с перерендерингом
  const componentId = useMemo(
    () => Math.random().toString(36).substring(7),
    []
  );

  const [cards, setCards] = useState([]);
  const [flipped, setFlipped] = useState([]);
  const [matched, setMatched] = useState([]);
  const [lockBoard, setLockBoard] = useState(false);
  const [firstCard, setFirstCard] = useState(null);
  const [allPairs, setAllPairs] = useState([]);
  const [error, setError] = useState(null);

  // Используем useRef для отслеживания, были ли уже инициализированы карточки
  const initialized = useRef(false);

  // Сохраняем предыдущие опции для сравнения
  const prevOptionsRef = useRef();

  // Обрабатываем опции и преобразуем их в корректный формат
  const processedOptions = useMemo(() => {
    try {
      // Используем утилиту валидации для проверки данных
      const validData = validateMemoryGameData(options, 'MemoryGame компонент');

      // Проверяем, валидны ли данные для игры (имеют пары карточек)
      if (validData.length === 0) {
        setError(t('memoryGame.noData'));
        console.warn('MemoryGame: ' + t('memoryGame.noData'), options);

        // Если нет данных и это режим разработки, создаем тестовые данные
        if (process.env.NODE_ENV === 'development') {
          console.info('MemoryGame: ' + t('memoryGame.createSampleData'));
          return createSampleMemoryGameData(3);
        }
        return [];
      }

      if (!isValidMemoryGameData(validData)) {
        console.warn('MemoryGame: ' + t('memoryGame.invalidPairs'), validData);
        setError(t('memoryGame.invalidPairs'));
        return validData; // Возвращаем данные, даже если они некорректны
      }

      setError(null);
      return validData;
    } catch (err) {
      console.error('MemoryGame: ' + t('memoryGame.optionsError'), err);
      setError(
        t('memoryGame.optionsErrorWithMessage', { message: err.message })
      );
      return [];
    }
  }, [options, t]);

  // Функция для создания эффекта салюта
  const fireworksEffect = () => {
    // Создаем дефолтные настройки для салюта
    const defaults = {
      startVelocity: 30,
      spread: 360,
      ticks: 60,
      zIndex: 0,
      disableForReducedMotion: true,
    };

    // Функция для запуска салюта в случайном месте
    const randomInRange = (min, max) => Math.random() * (max - min) + min;

    // Запускаем несколько "залпов" салюта в разных местах
    const fireworks = () => {
      const animationEnd = Date.now() + 1000;

      const interval = setInterval(() => {
        const timeLeft = animationEnd - Date.now();

        if (timeLeft <= 0) {
          return clearInterval(interval);
        }

        // Вычисляем случайные координаты
        const particleCount = 50 * (timeLeft / 1000);

        // Запускаем конфетти из двух мест
        confetti({
          ...defaults,
          particleCount,
          origin: { x: randomInRange(0.1, 0.3), y: randomInRange(0.4, 0.6) },
        });

        confetti({
          ...defaults,
          particleCount,
          origin: { x: randomInRange(0.7, 0.9), y: randomInRange(0.4, 0.6) },
        });
      }, 250);
    };

    fireworks();
  };

  // Инициализация карточек
  useEffect(() => {
    try {
      // Проверяем, что опции корректно обработаны и не пусты
      if (processedOptions.length === 0) {
        return; // Ошибка уже установлена в useMemo
      }

      // Проверяем, изменились ли опции
      const optionsChanged =
        prevOptionsRef.current &&
        JSON.stringify(prevOptionsRef.current) !==
          JSON.stringify(processedOptions);

      // Если опции изменились, или еще не были инициализированы
      if (!initialized.current || optionsChanged) {
        const initialCards = processedOptions.map((option, index) => ({
          id: index,
          text: option.text || t('memoryGame.emptyCard'),
          pairId: option.pairId || 0,
          originalIndex: index,
        }));

        // Перемешиваем карточки
        initialCards.sort(() => Math.random() - 0.5);
        setCards(initialCards);
        initialized.current = true;
        setError(null);

        // Сбрасываем состояние игры при изменении опций
        if (optionsChanged) {
          setFlipped([]);
          setMatched([]);
          setFirstCard(null);
          setAllPairs([]);
          setLockBoard(false);
        }
      }

      // Сохраняем текущие опции для будущего сравнения
      prevOptionsRef.current = processedOptions;
    } catch (err) {
      console.error(t('memoryGame.initError'), err);
      setError(t('memoryGame.initErrorWithMessage', { message: err.message }));
    }
  }, [processedOptions, t]);

  // Обновляем родительский компонент при изменении найденных пар
  useEffect(() => {
    if (allPairs.length > 0) {
      onChange(JSON.stringify(allPairs));
    }
  }, [allPairs, onChange]);

  const handleCardClick = (index) => {
    if (lockBoard) return;
    if (flipped.includes(index)) return;
    if (matched.includes(index)) return;

    // Переворачиваем карточку
    setFlipped((prev) => [...prev, index]);

    // Если это первая карточка из пары
    if (firstCard === null) {
      setFirstCard(index);
      return;
    }

    // Если это вторая карточка
    const firstCardObj = cards[firstCard];
    const secondCardObj = cards[index];

    // Проверяем совпадение
    if (firstCardObj.pairId === secondCardObj.pairId) {
      // Совпадение найдено
      setMatched((prev) => [...prev, firstCard, index]);

      // Добавляем пару в список найденных пар
      setAllPairs((prev) => [
        ...prev,
        {
          [firstCardObj.originalIndex]: firstCardObj.pairId,
          [secondCardObj.originalIndex]: secondCardObj.pairId,
        },
      ]);

      // Запускаем эффект салюта при нахождении пары
      fireworksEffect();

      resetBoard();
    } else {
      // Не совпадает, переворачиваем обратно
      setLockBoard(true);
      setTimeout(() => {
        setFlipped((prev) =>
          prev.filter((i) => i !== firstCard && i !== index)
        );
        resetBoard();
      }, 1000);
    }
  };

  const resetBoard = () => {
    setFirstCard(null);
    setLockBoard(false);
  };

  return (
    <Box sx={{ mt: 2 }} key={`memory-game-${componentId}`}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!error && cards.length > 0 && (
        <>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {cards.map((card, index) => (
              <Grid item xs={4} sm={3} key={`card-${componentId}-${index}`}>
                <Box
                  sx={{
                    height: 120,
                    perspective: '1000px',
                    cursor: matched.includes(index) ? 'default' : 'pointer',
                  }}
                  onClick={() =>
                    !matched.includes(index) &&
                    !lockBoard &&
                    handleCardClick(index)
                  }
                >
                  <Box
                    sx={{
                      position: 'relative',
                      width: '100%',
                      height: '100%',
                      transformStyle: 'preserve-3d',
                      transition: 'transform 0.6s',
                      transform: flipped.includes(index)
                        ? 'rotateY(180deg)'
                        : 'rotateY(0)',
                    }}
                  >
                    {/* Передняя сторона (рубашка) */}
                    <Paper
                      elevation={3}
                      sx={{
                        position: 'absolute',
                        width: '100%',
                        height: '100%',
                        backfaceVisibility: 'hidden',
                        backgroundColor: '#1976d2',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#fff',
                        '&:hover': matched.includes(index)
                          ? {}
                          : {
                              boxShadow: 6,
                            },
                      }}
                    >
                      <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                        ?
                      </Typography>
                    </Paper>

                    {/* Задняя сторона (содержимое) */}
                    <Paper
                      elevation={3}
                      sx={{
                        position: 'absolute',
                        width: '100%',
                        height: '100%',
                        backfaceVisibility: 'hidden',
                        transform: 'rotateY(180deg)',
                        backgroundColor: matched.includes(index)
                          ? '#e8f5e9'
                          : '#f5f5f5',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: 1,
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          textAlign: 'center',
                          color: '#000',
                          fontWeight: 'bold',
                          lineHeight: 1.2,
                          wordBreak: 'break-word',
                        }}
                      >
                        {card.text}
                      </Typography>
                    </Paper>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>

          <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
            {t('memoryGame.pairsFound', {
              found: allPairs.length,
              total: cards.length / 2,
            })}
          </Typography>
        </>
      )}
    </Box>
  );
};

export default MemoryGame;
